import { create } from "zustand";

const STORAGE_KEY = "voidex-favourites";
const SCHEMA_VERSION = 1;

interface FavouritesPersistence {
	version: number;
	ids: string[];
}

interface FavouritesState {
	favourites: Set<string>;
	isHydrated: boolean;

	toggleFavourite: (id: string) => void;
	isFavourite: (id: string) => boolean;
	hydrate: () => void;
}

function persist(favourites: Set<string>) {
	try {
		const data: FavouritesPersistence = {
			version: SCHEMA_VERSION,
			ids: [...favourites],
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch {
		// localStorage may be full or unavailable
	}
}

function loadPersisted(): Set<string> | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const data: FavouritesPersistence = JSON.parse(raw);
		if (data.version !== SCHEMA_VERSION) return null;
		return new Set(data.ids);
	} catch {
		return null;
	}
}

export const useFavouritesStore = create<FavouritesState>((set, get) => ({
	favourites: new Set(),
	isHydrated: false,

	toggleFavourite: (id) => {
		const next = new Set(get().favourites);
		if (next.has(id)) {
			next.delete(id);
		} else {
			next.add(id);
		}
		set({ favourites: next });
		persist(next);
	},

	isFavourite: (id) => get().favourites.has(id),

	hydrate: () => {
		const persisted = loadPersisted();
		if (persisted) {
			set({ favourites: persisted, isHydrated: true });
		} else {
			// Default favourite for new users
			const defaults = new Set(["sol-p3"]); // Earth
			set({ favourites: defaults, isHydrated: true });
			persist(defaults);
		}
	},
}));

// Selectors
export const useFavourites = () => useFavouritesStore((s) => s.favourites);
export const useIsFavourite = (id: string) => useFavouritesStore((s) => s.favourites.has(id));
